import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Divider,
  Typography,
} from '@mui/material';
import { format } from 'date-fns';
import { uk } from 'date-fns/locale';
import { BaseDialog } from '@components/common/BaseDialog.tsx';
import {
  AdaptiveTabs,
  AdaptiveTabItem,
} from '@components/common/AdaptiveTabs.tsx';
import { UserAvatar } from '@components/common/UserAvatar.tsx';
import { EventTypeIcon } from '@components/clients/calendar/EventTypeIcon';
import { ModalIndices } from '@/common/i-doc/i-doc.registry.ts';
import { CalendarEventDto } from '@/types/calendar';
import {
  getPmClientAiBatch,
  PmClientAiBatch,
} from '@services/pm-client-calendar.service.ts';
import { PmEventEnergy } from './PmEventEnergy';
import { PmClientAppPreview } from './PmClientAppPreview';

// Вікно перегляду події календаря для продакт-менеджера (2026-10-10). Одне на всі типи подій (включно з їжею), лише
// перегляд. Мета - дослідити, з чого складається подія і як відбувався розрахунок, тому блоки йдуть ланцюжком:
// "Що ввів клієнт" (текст AI-введення з батча / details.rawInput / продукт з бібліотеки / ручне введення) ->
// "Що вийшло після обробки" (основні поля, `details` деревом "ключ - значення") -> "Енергія" (прихід / витрата з
// розрахунком тими самими формулами, що й денний баланс) ->
// окрема вкладка "Технічне" (технічні поля і сирий JSON). Поки спільні поля для всіх типів; вигляд за типом події додаватимемо пізніше.

const STATUS_LABELS: Record<
  CalendarEventDto['status'],
  { label: string; color: 'warning' | 'success' | 'error' }
> = {
  pending: { label: 'Очікується', color: 'warning' },
  completed: { label: 'Виконано', color: 'success' },
  overdue: { label: 'Прострочено', color: 'error' },
};

const SOURCE_LABELS: Record<NonNullable<CalendarEventDto['source']>, string> = {
  manual: 'Вручну',
  ai: 'AI',
  system: 'Система',
};

const NUTRITION_STATUS_LABELS: Record<
  NonNullable<CalendarEventDto['nutritionStatus']>,
  string
> = {
  none: 'Немає',
  pending: 'Розраховується',
  provided: 'Вказано користувачем',
  calculated: 'Розраховано',
  failed: 'Помилка розрахунку',
};

// scheduledLdt - T3 (локальний час без зони, показуємо як є); completedAt/createdAt/updatedAt/notificationSentAt - T1 (UTC).
const formatDateTime = (value?: string | null) =>
  value ? format(new Date(value), 'dd MMMM yyyy, HH:mm', { locale: uk }) : '—';

const isEmptyValue = (value: unknown) =>
  value === null ||
  value === undefined ||
  value === '' ||
  (Array.isArray(value) && value.length === 0);

const isPrimitive = (value: unknown) =>
  value === null || ['string', 'number', 'boolean'].includes(typeof value);

const formatPrimitive = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'так' : 'ні';
  return String(value);
};

// Уніфіковане дерево для `details` будь-якого типу події: примітиви - текстом, списки примітивів - через кому,
// об'єкти й списки об'єктів - вкладеними блоками.
const DetailsTree: React.FC<{ value: unknown; depth?: number }> = ({
  value,
  depth = 0,
}) => {
  if (isPrimitive(value) || value === undefined) {
    return (
      <Typography variant='body2' sx={{ wordBreak: 'break-word' }}>
        {formatPrimitive(value)}
      </Typography>
    );
  }

  if (Array.isArray(value)) {
    if (value.length === 0) return <Typography variant='body2'>—</Typography>;
    if (value.every(isPrimitive)) {
      return (
        <Typography variant='body2' sx={{ wordBreak: 'break-word' }}>
          {value.map(formatPrimitive).join(', ')}
        </Typography>
      );
    }
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {value.map((item, index) => (
          <Box
            key={index}
            sx={{ pl: 1.5, borderLeft: '2px solid', borderColor: 'divider' }}
          >
            <Typography variant='caption' color='text.secondary'>
              #{index + 1}
            </Typography>
            <DetailsTree value={item} depth={depth + 1} />
          </Box>
        ))}
      </Box>
    );
  }

  const entries = Object.entries(value as Record<string, unknown>).filter(
    ([, v]) => !isEmptyValue(v)
  );
  if (entries.length === 0) return <Typography variant='body2'>—</Typography>;

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'minmax(110px, auto) 1fr',
        gap: '4px 16px',
        alignItems: 'start',
      }}
    >
      {entries.map(([key, v]) => (
        <React.Fragment key={key}>
          <Typography
            variant='body2'
            color='text.secondary'
            sx={{ fontFamily: 'monospace', fontSize: '0.8rem', pt: '1px' }}
          >
            {key}
          </Typography>
          <DetailsTree value={v} depth={depth + 1} />
        </React.Fragment>
      ))}
    </Box>
  );
};

const FieldGrid: React.FC<{ rows: Array<[string, React.ReactNode]> }> = ({
  rows,
}) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: 'auto 1fr',
      gap: '6px 16px',
      alignItems: 'center',
    }}
  >
    {rows.map(([label, content]) => (
      <React.Fragment key={label}>
        <Typography variant='body2' color='text.secondary'>
          {label}
        </Typography>
        {typeof content === 'string' ? (
          <Typography variant='body2'>{content}</Typography>
        ) : (
          content
        )}
      </React.Fragment>
    ))}
  </Box>
);

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <Typography variant='subtitle2' sx={{ mb: 1 }}>
    {children}
  </Typography>
);

const Quote: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    sx={{
      p: 1.5,
      borderLeft: '3px solid',
      borderColor: 'primary.main',
      bgcolor: 'action.hover',
      borderRadius: 1,
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
    }}
  >
    <Typography variant='body2'>{children}</Typography>
  </Box>
);

const quantityText = (d: CalendarEventDto['details']) => {
  if (!d) return '—';
  const parts = [
    d.quantity !== undefined && d.measureKey
      ? `${d.quantity} × ${d.measureKey}`
      : null,
    d.qty !== undefined ? `${d.qty} порц.` : null,
    d.qtyG !== undefined ? `${d.qtyG} г` : null,
  ].filter(Boolean);
  return parts.length ? parts.join(', ') : '—';
};

// "Що ввів клієнт": для AI - текст з батча (або details.rawInput), для бібліотеки - вибраний продукт і кількість,
// інакше - ручне введення (назва події).
const ClientInput: React.FC<{
  event: CalendarEventDto;
  batch: PmClientAiBatch | null;
  batchLoading: boolean;
  batchError: boolean;
}> = ({ event, batch, batchLoading, batchError }) => {
  if (batchLoading)
    return (
      <Box sx={{ py: 1 }}>
        <CircularProgress size={20} />
      </Box>
    );

  const rawInput = batch?.rawInput ?? event.details?.rawInput ?? null;
  if (rawInput) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Quote>{rawInput}</Quote>
        <Typography variant='caption' color='text.secondary'>
          Введено текстом / голосом, розпізнано AI
          {batch?.createdAt ? ` · ${formatDateTime(batch.createdAt)}` : ''}
          {batch?.recognitionStatus
            ? ` · розпізнавання: ${batch.recognitionStatus}`
            : ''}
        </Typography>
        {batch && batch.events.length > 1 && (
          <Box>
            <Typography variant='caption' color='text.secondary'>
              З цього введення створено подій: {batch.events.length}
            </Typography>
            <Box component='ul' sx={{ m: 0, pl: 2.5 }}>
              {batch.events.map(e => (
                <li key={e.id}>
                  <Typography
                    variant='body2'
                    fontWeight={e.id === event.id ? 700 : 400}
                  >
                    {e.title}
                    {e.id === event.id ? ' (ця подія)' : ''}
                  </Typography>
                </li>
              ))}
            </Box>
          </Box>
        )}
      </Box>
    );
  }

  if (event.batchId && batchError) {
    return (
      <Alert severity='warning' variant='outlined'>
        Не вдалося завантажити введення клієнта (batch {event.batchId}).
      </Alert>
    );
  }

  if (event.associationType === 'Food' && event.associationId) {
    return (
      <FieldGrid
        rows={[
          [
            'Вибрано з бібліотеки',
            `${event.title} (продукт #${event.associationId})`,
          ],
          ['Кількість', quantityText(event.details)],
        ]}
      />
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Quote>{event.title || '—'}</Quote>
      <Typography variant='caption' color='text.secondary'>
        {event.source === 'system' ? 'Створено системою' : 'Введено вручну'},
        без AI-обробки
      </Typography>
    </Box>
  );
};

// Вкладки вікна: сама подія (ланцюжок введення -> результат -> енергія), як її бачить клієнт у додатку, і технічне
// (поля + сирий JSON).
const TABS: AdaptiveTabItem[] = [
  { value: 'event', label: 'Подія' },
  { value: 'app', label: 'Як у додатку' },
  { value: 'technical', label: 'Технічне' },
];

interface PmCalendarEventModalProps {
  open: boolean;
  clientId: number;
  event: CalendarEventDto | null;
  onClose: () => void;
}

export const PmCalendarEventModal: React.FC<PmCalendarEventModalProps> = ({
  open,
  clientId,
  event,
  onClose,
}) => {
  const [batch, setBatch] = useState<PmClientAiBatch | null>(null);
  const [batchLoading, setBatchLoading] = useState(false);
  const [batchError, setBatchError] = useState(false);
  const [tab, setTab] = useState('event');

  useEffect(() => {
    setBatch(null);
    setBatchError(false);
    if (!event?.batchId) return;
    setBatchLoading(true);
    getPmClientAiBatch(clientId, event.batchId)
      .then(setBatch)
      .catch(() => setBatchError(true))
      .finally(() => setBatchLoading(false));
  }, [clientId, event?.id, event?.batchId]);

  if (!event) return null;

  const status = STATUS_LABELS[event.status];

  const mainRows: Array<[string, React.ReactNode]> = [
    [
      'Статус',
      <Chip
        key='status'
        size='small'
        label={status?.label ?? event.status}
        color={status?.color ?? 'default'}
        variant='outlined'
        sx={{ justifySelf: 'start' }}
      />,
    ],
    ['Заплановано', formatDateTime(event.scheduledLdt)],
    ['Виконано', formatDateTime(event.completedAt)],
    [
      'Автор',
      <Box key='author' sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <UserAvatar
          path={event.createdBy.avatar ?? undefined}
          name={event.createdBy.name}
          sx={{ width: 20, height: 20, fontSize: '10px' }}
        />
        <Typography variant='body2'>{event.createdBy.name}</Typography>
      </Box>,
    ],
    ['Створено', formatDateTime(event.createdAt)],
    ['Оновлено', formatDateTime(event.updatedAt)],
  ];

  const technicalRows: Array<[string, React.ReactNode]> = [
    [
      'Джерело',
      event.source ? (SOURCE_LABELS[event.source] ?? event.source) : '—',
    ],
    [
      'З бібліотеки',
      event.isStructured === undefined
        ? '—'
        : event.isStructured
          ? 'так (прив’язано до запису в БД)'
          : 'ні (вільний текст)',
    ],
    [
      'Статус КБЖВ',
      event.nutritionStatus
        ? (NUTRITION_STATUS_LABELS[event.nutritionStatus] ??
          event.nutritionStatus)
        : '—',
    ],
    [
      'Прив’язка',
      event.associationType
        ? `${event.associationType} #${event.associationId ?? '—'}`
        : '—',
    ],
    ['Batch', event.batchId ?? '—'],
    ['Вплив на енергію', String(event.energyImpact ?? '—')],
    ['Пуш надіслано', formatDateTime(event.notificationSentAt)],
    ['ID події / календаря', `${event.id} / ${event.calendarId}`],
  ];

  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      maxWidth='sm'
      iDoc={ModalIndices.PM_CALENDAR_EVENT}
      headerMedia={
        event.eventType.color ? (
          <Box sx={{ height: 4, bgcolor: event.eventType.color }} />
        ) : undefined
      }
      title={
        <Box
          sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}
        >
          <EventTypeIcon
            code={event.eventType.code}
            icon={event.eventType.icon}
            accentColor={event.eventType.color}
            size={22}
          />
          <Typography variant='h6' component='span' noWrap>
            {event.eventType.name || 'Подія'}
          </Typography>
        </Box>
      }
    >
      <AdaptiveTabs tabs={TABS} value={tab} onChange={setTab} />
      <Box sx={{ pt: 2 }}>
        {tab === 'event' && (
          <>
            <SectionTitle>1. Що ввів клієнт</SectionTitle>
            <ClientInput
              event={event}
              batch={batch}
              batchLoading={batchLoading}
              batchError={batchError}
            />

            <Divider sx={{ my: 2 }} />

            <SectionTitle>2. Що вийшло після обробки</SectionTitle>
            {event.title && (
              <Typography
                variant='body1'
                fontWeight={600}
                sx={{ mb: 1, wordBreak: 'break-word' }}
              >
                {event.title}
              </Typography>
            )}
            <FieldGrid rows={mainRows} />
            <Typography
              variant='body2'
              color='text.secondary'
              sx={{ mt: 1.5, mb: 0.5 }}
            >
              Деталі
            </Typography>
            <DetailsTree value={event.details ?? null} />

            <Divider sx={{ my: 2 }} />
            <SectionTitle>
              3. Енергія: прихід / витрата і як порахована
            </SectionTitle>
            <PmEventEnergy clientId={clientId} event={event} />
          </>
        )}

        {tab === 'app' && <PmClientAppPreview event={event} />}

        {tab === 'technical' && (
          <>
            <SectionTitle>Технічні поля</SectionTitle>
            <FieldGrid rows={technicalRows} />

            <Divider sx={{ my: 2 }} />

            <SectionTitle>Сирі дані (JSON)</SectionTitle>
            <Box
              component='pre'
              sx={{
                m: 0,
                p: 1.5,
                bgcolor: 'action.hover',
                borderRadius: 1,
                fontSize: '0.75rem',
                overflowX: 'auto',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {JSON.stringify(event, null, 2)}
            </Box>
          </>
        )}
      </Box>
    </BaseDialog>
  );
};
